/**
 * RegionService - Serviço de moeda e formatação de preços
 *
 * Usa GeolocationService para detecção de país
 * Gerencia moeda do usuário e formatação de valores
 */

import { geolocationService } from './geolocation.js';

// Moedas suportadas para pagamento (PayPal/Stripe)
const SUPPORTED_PAYMENT_CURRENCIES = ['BRL', 'USD', 'EUR', 'GBP', 'MXN', 'CAD'];

class RegionService {
  constructor() {
    this.userCurrency = null;
    this.initialized = false;
  }

  /**
   * Detecta país usando GeolocationService unificado
   */
  async detectCountry() {
    const geo = await geolocationService.detect();
    return geo.country;
  }

  /**
   * Chamado no signup para obter moeda inicial
   */
  async getInitialCurrency() {
    const geo = await geolocationService.detect();

    return {
      currency: geo.currency,
      symbol: geo.currencySymbol,
      country: geo.country,
      locale: geo.locale,
      confidence: geo.confidence
    };
  }

  /**
   * Define moeda do usuário (carregada do Firestore)
   */
  setUserCurrency(currency) {
    if (currency && typeof currency === 'string') {
      this.userCurrency = currency.toUpperCase();
      this.initialized = true;
    }
  }

  /**
   * Retorna moeda do usuário salva ou default
   */
  getUserCurrency() {
    return this.userCurrency || 'BRL';
  }

  /**
   * Verifica se tem moeda definida
   */
  hasCurrency() {
    return !!this.userCurrency;
  }

  /**
   * Retorna símbolo da moeda
   */
  getCurrencySymbol(currency = null) {
    const curr = currency || this.getUserCurrency();
    const symbols = {
      BRL: 'R$',
      USD: '$',
      EUR: '€',
      GBP: '£',
      MXN: '$',
      CAD: '$',
      ARS: '$',
      CLP: '$',
      COP: '$',
      PEN: 'S/',
      UYU: '$'
    };
    return symbols[curr] || '$';
  }

  /**
   * Formatação principal de preço
   */
  formatPrice(value, options = {}) {
    const currency = options.currency || this.getUserCurrency();
    const locale = this._getLocaleForCurrency(currency);

    if (value === null || value === undefined || isNaN(value)) {
      const symbol = this.getCurrencySymbol(currency);
      return currency === 'BRL' ? `${symbol} 0,00` : `${symbol}0.00`;
    }

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: options.compact ? 0 : 2,
        maximumFractionDigits: options.compact ? 0 : 2
      }).format(numValue);
    } catch {
      const symbol = this.getCurrencySymbol(currency);
      const formatted = numValue.toFixed(options.compact ? 0 : 2);
      return `${symbol} ${formatted}`;
    }
  }

  /**
   * Formato compacto (sem decimais quando inteiro)
   */
  formatPriceCompact(value, currency = null) {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    const isInteger = Number.isInteger(numValue);
    return this.formatPrice(value, { currency, compact: isInteger });
  }

  /**
   * Formato abreviado para valores grandes (K, M, B)
   */
  formatPriceShort(value, currency = null) {
    const curr = currency || this.getUserCurrency();
    const symbol = this.getCurrencySymbol(curr);
    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    if (numValue === null || numValue === undefined || isNaN(numValue)) {
      return `${symbol} 0`;
    }

    const absValue = Math.abs(numValue);
    const sign = numValue < 0 ? '-' : '';

    if (absValue >= 1000000000) {
      return `${sign}${symbol} ${(absValue / 1000000000).toFixed(1)}B`;
    }
    if (absValue >= 1000000) {
      return `${sign}${symbol} ${(absValue / 1000000).toFixed(1)}M`;
    }
    if (absValue >= 1000) {
      return `${sign}${symbol} ${(absValue / 1000).toFixed(1)}K`;
    }

    return this.formatPriceCompact(numValue, curr);
  }

  /**
   * Retorna locale apropriado para formatação da moeda
   */
  _getLocaleForCurrency(currency) {
    const map = {
      BRL: 'pt-BR',
      USD: 'en-US',
      EUR: 'de-DE',
      GBP: 'en-GB',
      MXN: 'es-MX',
      CAD: 'en-CA',
      ARS: 'es-AR',
      CLP: 'es-CL',
      COP: 'es-CO',
      PEN: 'es-PE',
      UYU: 'es-UY'
    };
    return map[currency] || 'en-US';
  }

  /**
   * Retorna moeda para uso em gateways de pagamento
   */
  getPaymentCurrency() {
    const currency = this.getUserCurrency();
    return SUPPORTED_PAYMENT_CURRENCIES.includes(currency) ? currency : 'USD';
  }

  /**
   * Verifica se moeda é suportada para pagamento
   */
  isPaymentCurrencySupported(currency = null) {
    const curr = currency || this.getUserCurrency();
    return SUPPORTED_PAYMENT_CURRENCIES.includes(curr);
  }
}

// Singleton
export const regionService = new RegionService();

// Exports de conveniência
export const formatPrice = (value, options) => regionService.formatPrice(value, options);
export const formatPriceCompact = (value, currency) => regionService.formatPriceCompact(value, currency);
export const formatPriceShort = (value, currency) => regionService.formatPriceShort(value, currency);
export const getCurrencySymbol = (currency) => regionService.getCurrencySymbol(currency);
